import {
  IHtmlData,
  ITextData,
  IField,
  IFormDocument,
} from 'app/services/api5-service/api.interface';
import { Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';

import { getLabel } from 'common/dynamic-form/wrappers/field-base/label/label.utils';
import { DocHelper } from 'utils/dochelper';

import { TextDataProps } from './model/textdata-props.interface';

import {
  ISPFieldConfig,
  ISPFieldType,
  ISPFieldWrapper,
  ISPFormState,
} from '../../model';
import { getImage, getImage$ } from '../../utils/get-image';

/**
 * Get content for textdata
 *
 * @param control - control (subfield) metadata
 * @param doc - doc instance
 */
function getContent(
  control: IHtmlData | ITextData,
  doc: IFormDocument,
): string | undefined {
  const name = control.$name;
  const type = '$type' in control ? control.$type : '';
  switch (type) {
    case 'msg':
      return DocHelper.getMessage(name, doc);
    case 'msgdata':
      return DocHelper.getMessage(DocHelper.getStringValue(name, doc), doc);
    case 'data':
      return DocHelper.getStringValue(name, doc);
    // because can be img with empty textdata with same name
    default:
      return;
  }
}

/**
 * Get content for htmldata
 *
 * @param control - control (subfield) metadata
 * @param doc - doc instance
 */
function getContentForHTMLData(
  control: IHtmlData | ITextData,
  doc: IFormDocument,
): string {
  const name = control.$name;
  return DocHelper.getStringValue(name, doc);
}

/**
 * Get content for textdata config
 *
 * @param control - control (subfield) metadata
 * @param field - field metadata
 * @param doc - doc instance
 */
export function getTextDataContent(
  control: IHtmlData | ITextData,
  field: IField,
  doc: IFormDocument,
): string | undefined {
  const isHtmlData = Boolean(field.htmldata);
  return isHtmlData
    ? getContentForHTMLData(control, doc)
    : getContent(control, doc);
}

/**
 * Get stream of textdata content
 *
 * @param control - control (subfield) metadata
 * @param field - field metadata
 * @param state - dynamic form state
 */
export function getTextDataContent$(
  control: IHtmlData | ITextData,
  field: IField,
  state: ISPFormState,
): Observable<string | undefined> {
  return state.docSubject.pipe(
    map(doc => getTextDataContent(control, field, doc)),
  );
}

/**
 * Check that textdata should be hidden
 *
 * @param control - control (subfield) metadata
 * @param field - field metadata
 * @param state - dynamic form state
 */
export function isTextDataShouldBeHidden(
  control: IHtmlData | ITextData,
  field: IField,
  state: ISPFormState,
): boolean {
  const content = getTextDataContent(control, field, state.docSubject.value);
  const img = getImage(field, state.docSubject.value);
  const label = getLabel(field, state.docSubject.value);
  const nothingToDisplay = !img && !content && !label;

  // @WARN there is cases, when textdata field is empty, but have label to display
  return (
    state.hiddenService.isHidden(control.$name, field.$name) || nothingToDisplay
  );
}

/**
 * Stream of textdata hidden state
 *
 * @param control - control (subfield) metadata
 * @param field - field metadata
 * @param state - dynamic form state
 */
export function isTextDataShouldBeHidden$(
  control: IHtmlData | ITextData,
  field: IField,
  state: ISPFormState,
): Observable<boolean> {
  return combineLatest([
    state.hiddenService.isHidden$(field.$name),
    state.docSubject,
  ]).pipe(map(() => isTextDataShouldBeHidden(control, field, state)));
}

/**
 * Get props for textdata field
 *
 * @param control - control (subfield) metadata
 * @param field - field metadata
 * @param state - dynamic form state
 */
export function getTextDataProps(
  control: IHtmlData | ITextData,
  field: IField,
  state: ISPFormState,
): TextDataProps {
  return {
    originalControl: control,
    originalField: field,
    content$: getTextDataContent$(control, field, state),
    isHidden: isTextDataShouldBeHidden(control, field, state),
    isHidden$: isTextDataShouldBeHidden$(control, field, state),
    img$: getImage$(field, state),
    isTextHighlight: '$warning' in control && control.$warning === 'yes',
  };
}

/**
 * Get config for textdata field
 *
 * @param control - control (subfield) metadata
 * @param field - field metadata
 * @param state - dynamic form state
 */
export function getTextDataConfig(
  control: IHtmlData | ITextData,
  field: IField,
  state: ISPFormState,
): ISPFieldConfig<ISPFieldType.TextData> {
  const props = getTextDataProps(control, field, state);

  return {
    type: ISPFieldType.TextData,
    wrappers: [ISPFieldWrapper.FieldBase],
    props,
  };
}
